import { get, map, maxBy } from "lodash";
import {
  PROCESS_TAGS,
  SUBSCRIPTION_STATUSES,
  TRACER_TYPES,
} from "utils/constants";

export const SIGNED_LIKE_PROCESS_TAGS = [
  PROCESS_TAGS.SIGNED,
  PROCESS_TAGS.SIGNED_INCOMPLETE,
  PROCESS_TAGS.SENT_TO_MANAGEMENT,
];

export const getLatestStatusEntry = (statuses = []) =>
  maxBy(
    statuses.filter(({ status }) => status?.id),
    ({ status }) => parseInt(status.order, 10) || 0,
  );

const statusOrder = (candidate) => parseInt(get(candidate, "order"), 10) || 0;

/**
 * A project can carry one row per journey, and their orders come from unrelated
 * graphs: the customer journey often sits higher than the advisor one while
 * describing something the advisor never did. The advisor screens list must
 * show his own step, so a manual tracer wins before comparing orders.
 */
const pickDisplayedMatch = (matches = []) => {
  const advisorMatches = matches.filter(
    ({ tracer }) => get(tracer, "type") === TRACER_TYPES.MANUAL,
  );
  return (
    maxBy(advisorMatches, ({ processStatus }) => statusOrder(processStatus)) ||
    maxBy(matches, ({ processStatus }) => statusOrder(processStatus))
  );
};

export const getLatestProjectStatus = (statuses = []) =>
  get(getLatestStatusEntry(statuses), "status", null);

export const matchStatusesWithProcess = (statuses = [], tracers = []) => {
  const result = map(
    statuses.filter(({ status }) => status),
    ({ status: { id: statusId, order } }) => {
      const allProcessStatuses = tracers.flatMap(
        (tracer) =>
          tracer?.flow?.process?.processStatuses
            ?.filter(({ id }) => id === statusId)
            .map((status) => ({
              ...status,
              tracer,
            })) || [],
      );
      const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
        return -Math.abs(candidate.order - order);
      });

      return {
        tracer: matchedStatus?.tracer || null,
        processStatus: matchedStatus || null,
      };
    },
  ).filter(({ tracer }) => tracer !== null);

  return pickDisplayedMatch(result);
};

/**
 * Resolves display status and navigation tracer for a project.
 * Falls back to API status fields and project.tracer when manual-tracer
 * matching fails (e.g. Web / external flows).
 */
export const resolveProjectStatus = (project, tracers = []) => {
  const statuses = get(project, "statuses", []);
  const matched = matchStatusesWithProcess(statuses, tracers);
  const rawStatus = getLatestProjectStatus(statuses);

  const processStatus = get(matched, "processStatus") || rawStatus;
  const tracer = get(matched, "tracer") || get(project, "tracer", null);

  return { processStatus, tracer };
};

export const canOpenProject = (project, tracers = []) => {
  const { tracer } = resolveProjectStatus(project, tracers);
  return !!get(project, "id") && !!get(tracer, "id");
};

export const isSignedLikeProject = (project, tracers = []) => {
  const { processStatus } = resolveProjectStatus(project, tracers);
  if (SIGNED_LIKE_PROCESS_TAGS.includes(processStatus?.tag)) {
    return true;
  }
  return get(project, "subscriptions", []).some(
    ({ status }) => status === SUBSCRIPTION_STATUSES.SIGNED,
  );
};
